import {
  ActionIcon,
  Box,
  Card,
  Divider,
  Group,
  Skeleton,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { useEffect, useState } from "react";
import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconChevronUp,
} from "@tabler/icons-react";
import Moment from "react-moment";
import { AppConstants, getCompanyTableColumns } from "utils/constants";
import { MetaLabel } from "core/components/metaLabel/MetaLabel";
import { EmptyState } from "../emptyState/EmptyState";
import { getClientLabel } from "utils/helper";
import DateDisplay from "../dateDisplay/DateDisplay";

const StyleInfoSections = ({
  selectedItem,
  user,
  loading = false,
  storageKey = "ql",
}) => {
  const theme = useMantineTheme();

  // Storage keys
  const metaInfoKey = `op-styleInfoSections_${storageKey}_showMetaInfo`;
  const seasonInfoKey = `op-styleInfoSections_${storageKey}_showSeasonInfo`;
  const sessionInfoKey = `op-styleInfoSections_${storageKey}_showSessionInfo`;

  // Initialize state from localStorage or default to true
  const [showMetaInfo, setShowMetaInfo] = useState(() => {
    const stored = localStorage.getItem(metaInfoKey);
    return stored !== null ? JSON.parse(stored) : true;
  });
  const [showSeasonInfo, setShowSeasonInfo] = useState(() => {
    const stored = localStorage.getItem(seasonInfoKey);
    return stored !== null ? JSON.parse(stored) : true;
  });
  const [showSessionInfo, setShowSessionInfo] = useState(() => {
    const stored = localStorage.getItem(sessionInfoKey);
    return stored !== null ? JSON.parse(stored) : true;
  });

  const [selectedMastersIdx, setSelectedMastersIdx] = useState(0);

  // Save to localStorage when state changes
  useEffect(() => {
    localStorage.setItem(metaInfoKey, JSON.stringify(showMetaInfo));
  }, [showMetaInfo, metaInfoKey]);

  useEffect(() => {
    localStorage.setItem(seasonInfoKey, JSON.stringify(showSeasonInfo));
  }, [showSeasonInfo, seasonInfoKey]);

  useEffect(() => {
    localStorage.setItem(sessionInfoKey, JSON.stringify(showSessionInfo));
  }, [showSessionInfo, sessionInfoKey]);

  const toggleShowMetaInfo = () => {
    setShowMetaInfo(!showMetaInfo);
  };

  const toggleShowSeasonInfo = () => {
    setShowSeasonInfo(!showSeasonInfo);
  };

  const toggleShowSessionInfo = () => {
    setShowSessionInfo(!showSessionInfo);
  };

  // Reset master index when selected item changes
  useEffect(() => {
    setSelectedMastersIdx(0);
  }, [selectedItem?.styleNumber]);

  const columns = getCompanyTableColumns(user?.clientCode, "mastersInfo");

  return (
    <>
      <Divider my="md" />

      {/* Master Style Information */}
      <Box
        style={{
          maxHeight: showMetaInfo ? 1800 : "35px",
          overflow: "hidden",
          transition: "max-height .3s ease-in-out",
        }}
      >
        <Group
          mb="md"
          style={{ cursor: "pointer" }}
          align="flex-start"
          justify="space-between"
          onClick={() => toggleShowMetaInfo(!showMetaInfo)}
        >
          <Title className="main-content-heading" order={6}>
            Master Records
          </Title>
          <ActionIcon size="sm" variant="default">
            {showMetaInfo ? (
              <IconChevronUp
                size={AppConstants.ICON_SIZE}
                stroke={AppConstants.ICON_STROKE_WIDTH}
              />
            ) : (
              <IconChevronDown
                size={AppConstants.ICON_SIZE}
                stroke={AppConstants.ICON_STROKE_WIDTH}
              />
            )}
          </ActionIcon>
        </Group>
        {/* show the meta info from the item */}
        {columns &&
        columns.length > 0 &&
        (loading ||
          (selectedItem &&
            selectedItem.masters &&
            selectedItem.masters.length > 0)) ? (
          <Stack gap={"sm"} mb="md">
            {columns.map((column) => (
              <Group key={column.accessorKey} align="flex-start">
                <MetaLabel c="dimmed">{column.header}</MetaLabel>

                {loading ? (
                  <Skeleton height={20} width="60%" radius="sm" />
                ) : (
                  <Text
                    size="sm"
                    style={{ width: "60%" }}
                    c={
                      selectedItem.masters[selectedMastersIdx] &&
                      selectedItem.masters[selectedMastersIdx][
                        column.accessorKey
                      ] &&
                      (typeof selectedItem.masters[selectedMastersIdx][
                        column.accessorKey
                      ] === "string"
                        ? selectedItem.masters[selectedMastersIdx][
                            column.accessorKey
                          ].length > 0
                        : selectedItem.masters[selectedMastersIdx][
                            column.accessorKey
                          ] != null)
                        ? "inherit"
                        : "dimmed"
                    }
                  >
                    {selectedItem.masters[selectedMastersIdx] &&
                    selectedItem.masters[selectedMastersIdx][
                      column.accessorKey
                    ] &&
                    (typeof selectedItem.masters[selectedMastersIdx][
                      column.accessorKey
                    ] === "string"
                      ? selectedItem.masters[selectedMastersIdx][
                          column.accessorKey
                        ].length > 0
                      : selectedItem.masters[selectedMastersIdx][
                          column.accessorKey
                        ] != null)
                      ? selectedItem.masters[selectedMastersIdx][
                          column.accessorKey
                        ]
                      : "-"}
                  </Text>
                )}
              </Group>
            ))}

            {!loading && selectedItem.masters.length > 1 && (
              <Group mt="md" justify="space-between" align="center">
                <Text size="sm" c="dimmed">
                  {selectedMastersIdx + 1} of {selectedItem.masters.length}{" "}
                  Master Records
                </Text>
                <Group gap="xs">
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    disabled={selectedMastersIdx === 0}
                    onClick={() =>
                      setSelectedMastersIdx(selectedMastersIdx - 1)
                    }
                    style={{
                      opacity: selectedMastersIdx === 0 ? 0.5 : 1,
                      cursor:
                        selectedMastersIdx === 0 ? "not-allowed" : "pointer",
                    }}
                  >
                    <IconChevronLeft size={16}
                    stroke={AppConstants.ICON_STROKE_WIDTH}
                    color={
                      theme.colorScheme === "dark"
                        ? theme.colors.dark[0]
                        : theme.colors.dark[9]
                    } />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    color={
                      theme.colorScheme === "dark"
                        ? theme.colors.dark[0]
                        : theme.colors.gray[9]
                    }
                    disabled={
                      selectedMastersIdx === selectedItem.masters.length - 1
                    }
                    onClick={() =>
                      setSelectedMastersIdx(selectedMastersIdx + 1)
                    }
                    style={{
                      opacity:
                        selectedMastersIdx === selectedItem.masters.length - 1
                          ? 0.5
                          : 1,
                      cursor:
                        selectedMastersIdx === selectedItem.masters.length - 1
                          ? "not-allowed"
                          : "pointer",
                    }}
                  >
                    <IconChevronRight
                      size={16}
                      stroke={AppConstants.ICON_STROKE_WIDTH}
                      color={
                        theme.colorScheme === "dark"
                          ? theme.colors.dark[0]
                          : theme.colors.dark[9]
                      }
                    />
                  </ActionIcon>
                </Group>
              </Group>
            )}
          </Stack>
        ) : <>
        
        {/* <Divider mb="md" /> */}
      <EmptyState 
        caption="No Master Records available"
      />
      
      </>}
      </Box>

      {/* Event Information (Season) - Show if loading or data exists */}
      {(loading ||
        (selectedItem?.seasons && selectedItem.seasons.length > 0)) && (
        <>
          <Divider mb="md" />
          <Box
            style={{
              maxHeight: showSeasonInfo ? 1800 : "35px",
              overflow: "hidden",
              transition: "max-height .3s ease-in-out",
            }}
          >
            <Group
              mb="md"
              style={{ cursor: "pointer" }}
              align="flex-start"
              justify="space-between"
              onClick={() => toggleShowSeasonInfo(!showSeasonInfo)}
            >
              <Title className="main-content-heading" order={6}>
                Associated Events
              </Title>
              <ActionIcon size="sm" variant="default">
                {showSeasonInfo ? (
                  <IconChevronUp
                    size={AppConstants.ICON_SIZE}
                    stroke={AppConstants.ICON_STROKE_WIDTH}
                  />
                ) : (
                  <IconChevronDown
                    size={AppConstants.ICON_SIZE}
                    stroke={AppConstants.ICON_STROKE_WIDTH}
                  />
                )}
              </ActionIcon>
            </Group>
            <Stack gap="md" mb="xl">
              {loading ? (
                // Skeleton loader for seasons
                <>
                  {[1, 2].map((idx) => (
                    <Card
                      key={`season-skeleton-${idx}`}
                      p="md"
                      withBorder
                      style={{
                        backgroundColor: "transparent",
                      }}
                    >
                      <Stack gap="sm">
                        <Group justify="space-between" align="flex-start">
                          <Box style={{ flex: 1 }}>
                            <Skeleton
                              height={18}
                              width="60%"
                              mb={8}
                              radius="sm"
                            />
                            <Skeleton height={16} width="40%" radius="sm" />
                          </Box>
                        </Group>
                        <Divider />
                        <Group
                          gap="xs"
                          wrap="wrap"
                          justify="space-between"
                          grow={true}
                        >
                          <Box>
                            <Skeleton
                              height={14}
                              width={50}
                              mb={4}
                              radius="sm"
                            />
                            <Skeleton height={16} width={80} radius="sm" />
                          </Box>
                          <Box>
                            <Skeleton
                              height={14}
                              width={60}
                              mb={4}
                              radius="sm"
                            />
                            <Skeleton height={16} width={70} radius="sm" />
                          </Box>
                          <Box>
                            <Skeleton
                              height={14}
                              width={70}
                              mb={4}
                              radius="sm"
                            />
                            <Skeleton height={16} width={90} radius="sm" />
                          </Box>
                        </Group>
                        <Group
                          align="flex-start"
                          justify="space-between"
                          grow={true}
                          gap="xs"
                        >
                          <Box>
                            <Skeleton
                              height={14}
                              width={70}
                              mb={4}
                              radius="sm"
                            />
                            <Skeleton height={16} width={100} radius="sm" />
                          </Box>
                          <Box>
                            <Skeleton
                              height={14}
                              width={60}
                              mb={4}
                              radius="sm"
                            />
                            <Skeleton height={16} width={100} radius="sm" />
                          </Box>
                          <Box></Box>
                        </Group>
                      </Stack>
                    </Card>
                  ))}
                </>
              ) : (
                selectedItem.seasons.map((season, idx) => (
                  <Card
                    key={season.id || season.seasonPart || idx}
                    p="md"
                    withBorder
                    style={{
                      backgroundColor: "transparent",
                    }}
                  >
                    <Stack gap="sm">
                      <Group justify="space-between" align="flex-start">
                        <Box style={{ flex: 1 }}>
                          <Text size="sm" fw={600} mb={4}>
                            {season.Name || "-"}
                          </Text>
                          {season.seasonPart && (
                            <Text size="sm" c="dimmed">
                              {season.seasonPart}
                            </Text>
                          )}
                        </Box>
                      </Group>

                      <Divider />

                      <Group
                        gap="xs"
                        wrap="wrap"
                        justify="space-between"
                        grow={true}
                        align="flex-start"
                      >
                        <Box>
                          <MetaLabel>Code</MetaLabel>
                          <Text size="sm" mt={4}>
                            {season.seasonPart || "-"}
                          </Text>
                        </Box>
                        {season.channels && (
                          <Box>
                            <MetaLabel>Channels</MetaLabel>
                            <Text size="sm" mt={4}>
                              {season.channels || "-"}
                            </Text>
                          </Box>
                        )}
                        <Box>
                          <MetaLabel>Department</MetaLabel>
                          <Text size="sm" mt={4}>
                            {season.DeptName || season.Dept || "-"}
                          </Text>
                        </Box>
                      </Group>
                      <Group
                        align="flex-start"
                        justify="space-between"
                        grow={true}
                        gap="xs"
                      >
                        <Box>
                          <MetaLabel width={90}>{getClientLabel(user.clientCode, 'otbDate')}</MetaLabel>
                          <Text
                            size="sm"
                            mt={4}
                            c={season.OTB ? "inherit" : "dimmed"}
                          >
                            {season.OTB ? (
                              <DateDisplay
                                value={season.OTB}
                                parse="YYYY-MM-DD"
                                format="DD/MM/YYYY"
                                trim
                              />
                            ) : (
                              "-"
                            )}
                          </Text>
                        </Box>
                        {season.scanned && (
                          <Box>
                            <MetaLabel>Scanned</MetaLabel>
                            <Text
                              size="sm"
                              mt={4}
                              c={season.scanned ? "inherit" : "dimmed"}
                            >
                              
                              {season.scanned ? season.scanned : "-"}
                            </Text>
                          </Box>
                        )}
                        <Box></Box>
                      </Group>
                    </Stack>
                  </Card>
                ))
              )}
            </Stack>
          </Box>
        </>
      )}

      {/* Session Information - Show if loading or data exists */}
      {(loading ||
        (selectedItem?.sessions && selectedItem.sessions.length > 0)) && (
        <>
          <Divider mb="md" />
          <Box
            style={{
              maxHeight: showSessionInfo ? 1800 : "35px",
              overflow: "hidden",
              transition: "max-height .3s ease-in-out",
            }}
          >
            <Group
              mb="md"
              style={{ cursor: "pointer" }}
              align="flex-start"
              justify="space-between"
              onClick={() => toggleShowSessionInfo(!showSessionInfo)}
            >
              <Title className="main-content-heading" order={6}>
                Associated Sessions
              </Title>
              <ActionIcon size="sm" variant="default">
                {showSessionInfo ? (
                  <IconChevronUp
                    size={AppConstants.ICON_SIZE}
                    stroke={AppConstants.ICON_STROKE_WIDTH}
                  />
                ) : (
                  <IconChevronDown
                    size={AppConstants.ICON_SIZE}
                    stroke={AppConstants.ICON_STROKE_WIDTH}
                  />
                )}
              </ActionIcon>
            </Group>
            <Stack gap="md" mb="xl">
              {loading ? (
                // Skeleton loader for sessions
                <>
                  {[1, 2].map((idx) => (
                    <Card
                      key={`session-skeleton-${idx}`}
                      p="md"
                      withBorder
                      style={{
                        backgroundColor: "transparent",
                      }}
                    >
                      <Stack gap="sm">
                        <Group justify="space-between" align="flex-start">
                          <Box style={{ flex: 1 }}>
                            <Skeleton
                              height={18}
                              width="60%"
                              mb={8}
                              radius="sm"
                            />
                            <Skeleton height={16} width="40%" radius="sm" />
                          </Box>
                        </Group>
                        <Divider />
                        <Group gap="xl" wrap="wrap" justify="space-between">
                          <Box>
                            <Skeleton
                              height={14}
                              width={80}
                              mb={4}
                              radius="sm"
                            />
                            <Skeleton height={16} width={100} radius="sm" />
                          </Box>
                          <Box>
                            <Skeleton
                              height={14}
                              width={50}
                              mb={4}
                              radius="sm"
                            />
                            <Skeleton height={16} width={80} radius="sm" />
                          </Box>
                        </Group>
                        <Group
                          align="flex-start"
                          justify="space-between"
                          grow={true}
                          gap="xs"
                        >
                          <Box>
                            <Skeleton
                              height={14}
                              width={70}
                              mb={4}
                              radius="sm"
                            />
                            <Skeleton height={16} width={100} radius="sm" />
                          </Box>
                          <Box>
                            <Skeleton
                              height={14}
                              width={60}
                              mb={4}
                              radius="sm"
                            />
                            <Skeleton height={16} width={100} radius="sm" />
                          </Box>
                          <Box>
                            <Skeleton
                              height={14}
                              width={60}
                              mb={4}
                              radius="sm"
                            />
                            <Skeleton height={16} width={100} radius="sm" />
                          </Box>
                        </Group>
                      </Stack>
                    </Card>
                  ))}
                </>
              ) : (
                selectedItem.sessions.map((session, idx) => (
                  <Card
                    key={session.id || session.sessionName || idx}
                    p="md"
                    withBorder
                    style={{
                      backgroundColor: "transparent",
                    }}
                  >
                    <Stack gap="sm">
                      <Group justify="space-between" align="flex-start">
                        <Box style={{ flex: 1 }}>
                          <Text size="sm" fw={600} mb={4}>
                            {session.sessionName || "-"}
                          </Text>
                          <Text size="sm" c="dimmed">
                            {session.name || "-"}
                          </Text>
                        </Box>
                      </Group>

                      <Divider />

                      <Group gap="xl" wrap="wrap" justify="space-between">
                        <Box>
                          <MetaLabel>Department</MetaLabel>
                          <Text size="sm" mt={4}>
                            {session.department || "-"}
                          </Text>
                        </Box>
                        <Box>
                          <MetaLabel>Code</MetaLabel>
                          <Text size="sm" mt={4}>
                            {session.ssPart || "-"}
                          </Text>
                        </Box>
                      </Group>
                      <Group
                        align="flex-start"
                        justify="space-between"
                        grow={true}
                        gap="xs"
                      >
                        <Box>
                          <MetaLabel width={70}>{getClientLabel(user.clientCode, 'otbDate')}</MetaLabel>
                          <Text
                            size="sm"
                            mt={4}
                            c={session.otbDate ? "inherit" : "dimmed"}
                          >
                            {session.otbDate ? (
                              <DateDisplay
                                value={session.otbDate}
                                parse="YYYY-MM-DD"
                                format="DD/MM/YYYY"
                                trim
                              />
                            ) : (
                              "-"
                            )}
                          </Text>
                        </Box>
                        <Box>
                          <MetaLabel>Created</MetaLabel>
                          <Text
                            size="sm"
                            mt={4}
                            c={session.created ? "inherit" : "dimmed"}
                          >
                            {session.created ? (
                              <DateDisplay
                                value={session.created}
                                parse="YYYY-MM-DD-HH-mm-ss"
                                format="DD/MM/YYYY"
                                trim
                              />
                            ) : (
                              "-"
                            )}
                          </Text>
                        </Box>
                        <Box>
                          <MetaLabel>Deadline</MetaLabel>
                          <Text
                            size="sm"
                            mt={4}
                            c={session.deadline ? "inherit" : "dimmed"}
                          >
                            {session.deadline ? (
                              <DateDisplay
                                value={session.deadline}
                                parse="YYYY-MM-DD"
                                format="DD/MM/YYYY"
                                trim
                              />
                            ) : (
                              "-"
                            )}
                          </Text>
                        </Box>
                      </Group>
                    </Stack>
                  </Card>
                ))
              )}
            </Stack>
          </Box>
        </>
      )}
    </>
  );
};

export default StyleInfoSections;
